import { MOVE_OVERVIEW_PATH, SCAFFOLDER_TASKS_PATH } from '../move/model';

export type CapabilityStatus =
  | 'Available'
  | 'Planning only'
  | 'Shared foundation'
  | 'Planned';
export type CapabilityIcon =
  | 'assess'
  | 'move'
  | 'adapt'
  | 'prove'
  | 'inventory'
  | 'guidance'
  | 'history'
  | 'notifications'
  | 'assurance';

export type CapabilityModule = {
  id: string;
  title: string;
  path: string;
  icon: CapabilityIcon;
  status: CapabilityStatus;
  description: string;
  outcome: string;
  // Informational destinations never create or authorize an operation.
  roadmap?: {
    purpose: string;
    boundary: string;
    requirements: readonly string[];
  };
};

export type PlatformCapability = {
  id: 'migrate' | 'operate';
  title: string;
  path: string;
  eyebrow: string;
  promise: string;
  description: string;
  entry: string;
  outcome: string;
  availability: string;
  intro: string;
  modules: readonly CapabilityModule[];
};

/** Product navigation, not workflow state or an authorization policy.
 * Add modules here; pages and sidebar links consume the same hierarchy.
 * Existing destination pages retain their own server-enforced permissions.
 */
export const PLATFORM_CAPABILITIES: readonly PlatformCapability[] = [
  {
    id: 'migrate',
    title: 'MIGRATE',
    path: '/migrate',
    eyebrow: 'Change with confidence',
    promise: 'Make the move provable.',
    description:
      'From understanding a workload to proving it belongs in its new home. One governed journey, with evidence at every boundary.',
    entry: 'A workload, a business need and a destination.',
    outcome: 'A proven, operable service with retained evidence.',
    availability: 'ASSESS available · MOVE planning available',
    intro:
      'Four stages. Each has a distinct purpose, a decision boundary and an artifact to carry forward. These are capabilities, not the progress of a live migration.',
    modules: [
      {
        id: 'assess',
        title: 'ASSESS',
        path: '/create',
        icon: 'assess',
        status: 'Available',
        description:
          'Understand the source, choose a destination and review whether the workload is ready.',
        outcome: 'Evaluation, frozen baseline and independent review.',
      },
      {
        id: 'move',
        title: 'MOVE',
        path: MOVE_OVERVIEW_PATH,
        icon: 'move',
        status: 'Planning only',
        description:
          'Bring the approved assessment into a migration plan. Explore the execution roadmap.',
        outcome: 'An immutable draft plan. No replication or cutover.',
      },
      {
        id: 'adapt',
        title: 'ADAPT',
        path: '/migrate/adapt',
        icon: 'adapt',
        status: 'Planned',
        description:
          'Make the migrated target secure, observable and ready to operate in the cloud.',
        outcome: 'Planned: a hardened, instrumented target.',
        roadmap: {
          purpose: 'Turn an as-moved workload into a cloud-operable service.',
          boundary:
            'There is no executable ADAPT workflow in this release. This page describes the intended capability; it does not change a target or apply infrastructure.',
          requirements: [
            'An accepted test target and its MOVE evidence.',
            'A reviewed infrastructure and configuration plan.',
            'Policy checks and an explicit approval before changes.',
          ],
        },
      },
      {
        id: 'prove',
        title: 'PROVE',
        path: '/migrate/prove',
        icon: 'prove',
        status: 'Planned',
        description:
          'Verify the target against its agreed baseline and retain the evidence of acceptance.',
        outcome: 'Planned: a signed service-acceptance evidence pack.',
        roadmap: {
          purpose:
            'Demonstrate that the migrated service meets its agreed acceptance criteria.',
          boundary:
            'There is no executable PROVE workflow in this release. Assessment records and signed container images are not a completed service-acceptance evidence pack.',
          requirements: [
            'The frozen application baseline and executable acceptance tests.',
            'Results from the moved and adapted target.',
            'An approved release decision and signed, retained evidence.',
          ],
        },
      },
    ],
  },
  {
    id: 'operate',
    title: 'OPERATE',
    path: '/operate',
    eyebrow: 'Confidence that continues',
    promise: 'Keep it provable.',
    description:
      'The long-term home for service knowledge and operational assurance. Keep the context, revisit the proof and prepare for what comes next.',
    entry: 'An accepted service, its evidence and runbooks.',
    outcome: 'Ongoing assurance and a rehearsed path to recovery or exit.',
    availability: 'Shared tools available · operational workflows planned',
    intro:
      'Start with the shared tools already in Horizon. They provide context and workflow visibility; they are not a live monitoring, incident-response or recovery service.',
    modules: [
      {
        id: 'inventory',
        title: 'Workload inventory',
        path: '/catalog?filters%5Bkind%5D=resource&filters%5Btype%5D=virtual-machine&filters%5Buser%5D=all',
        icon: 'inventory',
        status: 'Shared foundation',
        description:
          'Find workload records visible to your tenant, including their ownership and discovered facts.',
        outcome: 'Catalog context, not live service-health monitoring.',
      },
      {
        id: 'guidance',
        title: 'Guidance library',
        path: '/docs',
        icon: 'guidance',
        status: 'Shared foundation',
        description:
          'Read the documentation and assessment guidance already published in Horizon.',
        outcome:
          'Existing guidance; operational runbooks are not yet delivered.',
      },
      {
        id: 'history',
        title: 'Workflow history',
        path: SCAFFOLDER_TASKS_PATH,
        icon: 'history',
        status: 'Shared foundation',
        description:
          'Return to your workflow tasks and inspect their recorded outputs across stages.',
        outcome:
          'Your authorized task history, not a cross-user operations queue.',
      },
      {
        id: 'notifications',
        title: 'Notifications',
        path: '/notifications',
        icon: 'notifications',
        status: 'Shared foundation',
        description:
          'Read Horizon notifications, including assigned assessment reviews.',
        outcome: 'Workflow notifications, not infrastructure alerts.',
      },
      {
        id: 'assurance',
        title: 'Continuous assurance',
        path: '/operate/assurance',
        icon: 'assurance',
        status: 'Planned',
        description:
          'Explore how service watching, re-verification and recovery rehearsals fit into OPERATE.',
        outcome: 'Planned: recurring proof of operability and portability.',
        roadmap: {
          purpose:
            'Keep an accepted service operable, and keep its recovery and exit path provable.',
          boundary:
            'Monitoring, re-verification and recovery rehearsals are not active in this release. Existing Catalog, Docs and Notifications plugins do not establish these guarantees.',
          requirements: [
            'Accepted service ownership, evidence and maintained runbooks.',
            'Defined service checks and a cadence for re-verification.',
            'Separately authorized recovery or portability rehearsals.',
          ],
        },
      },
    ],
  },
];

export const CAPABILITY_ROADMAPS = PLATFORM_CAPABILITIES.flatMap(capability =>
  capability.modules
    .filter(module => module.roadmap)
    .map(module => ({ capability, module })),
);
